export type HubTaskStatus = "todo" | "inprogress" | "done" | "overdue";

export interface TaskStep {
  num: number;
  title: string;
  detail: string;
  tip?: string;
}

export interface TaskCollaborator {
  team: string;
  role: string;
  contactTip?: string;
}

/** 공고문 / 지침서 / 기준 문서 */
export interface AnnouncementDoc {
  title: string;
  issuer: string;
  issueDate: string;
  deadline?: string;
  sections: { heading: string; content: string }[];
}

/** 직접 작성해볼 수 있는 연습 양식 필드 */
export interface PracticeField {
  id: string;
  label: string;
  type: "text" | "textarea" | "number" | "date" | "select";
  placeholder?: string;
  options?: string[];
  required?: boolean;
  hint?: string;
  maxLength?: number;
}

/** 직접 작성해볼 수 있는 연습 양식 (사업계획서, 분석 보고서 등) */
export interface PracticeForm {
  title: string;
  subtitle?: string;
  fields: PracticeField[];
}

export interface HubTask {
  id: string;
  title: string;
  detail: string;
  dueDate: string;
  dDay: number;
  status: HubTaskStatus;
  assignee: string;
  deliverable?: string;
  approvalTemplateId?: string;
  steps?: TaskStep[];
  caution?: string;
  reference?: string;
  isCollaboration?: boolean;
  collaborators?: TaskCollaborator[];
  /** 분석해야 할 공고문/기준 문서 */
  announcement?: AnnouncementDoc;
  /** 직접 작성해볼 수 있는 연습 양식 */
  practiceForm?: PracticeForm;
}

// ── A&R 허브 ────────────────────────────────────────────
export const arTasks: HubTask[] = [
  {
    id: "ar1",
    title: "루나&스타즈 2nd EP 앨범 기획서 작성",
    detail: "콘셉트 방향성(팝·R&B 크로스오버), 타이틀곡 후보 3곡 선정, 예상 발매 일정 및 마케팅 예산안 포함. 브랜딩팀·마케팅팀 협의 후 최종본 제출.",
    dueDate: "2026.04.07",
    dDay: 11,
    status: "inprogress",
    assignee: "이민준 A&R (나)",
    deliverable: "앨범 기획서 PPT (20슬라이드)",
    approvalTemplateId: "tmpl5",
    steps: [
      {
        num: 1,
        title: "아티스트 현황 데이터 수집",
        detail: "최근 6개월 스트리밍 통계, SNS 팔로워 성장률, 팬 커뮤니티(디씨·팬카페) 반응 키워드를 정리하세요. 멜론 아티스트 통계 페이지에서 핵심 청취층(연령·성별 분포) 확인이 필수입니다.",
        tip: "경쟁 아티스트의 발매 예정 일정도 같이 체크하세요. 비슷한 콘셉트가 같은 달에 나오면 시장 포화로 묻힐 수 있어요.",
      },
      {
        num: 2,
        title: "콘셉트 방향 3안 도출",
        detail: "브레인스토밍으로 방향성 후보 3개를 작성합니다. 각 안에는 레퍼런스 아티스트 2~3팀, 예상 타겟층, 장르와 BPM 범위를 포함해야 팀원들이 구체적으로 비교·선택할 수 있어요.",
        tip: "단순 '밝은 콘셉트' 같은 추상적 표현 대신 '90년대 시티팝 감성 × 2020년대 걸그룹 에너지'처럼 구체적으로 쓰세요.",
      },
      {
        num: 3,
        title: "예산 항목별 세분화",
        detail: "제작비(녹음·편곡·세션), MV 제작비, 사진·비주얼 제작비, 홍보·광고비로 구분해 항목별 상한선을 설정하세요. 총액이 아닌 항목별 배분이 있어야 CFO 검토를 통과할 수 있습니다.",
        tip: "처음 제출하는 예산안은 실제 필요 금액보다 10~15% 높게 잡으세요. 결재 과정에서 삭감되는 경우가 많습니다.",
      },
      {
        num: 4,
        title: "발매 타임라인 역산 작성",
        detail: "최종 발매일에서 거꾸로 계산합니다. 음원 유통 신청(D-14), 마스터링 완료(D-21), 보컬 레코딩(D-45), 작곡 최종 선정(D-60). 각 단계에 담당자를 명시해야 책임 소재가 분명해져요.",
      },
    ],
    caution: "기존 앨범과 세계관 일관성을 반드시 확인하세요. 팬덤이 쌓아온 세계관을 갑자기 바꾸면 기존 팬층 이탈 원인이 됩니다. 변화가 필요하다면 점진적 전환 서사를 기획서에 포함하세요.",
    reference: "멜론 아티스트 통계 / 한터차트 월간 리포트 / 유사 콘셉트 앨범 가온차트 성과 데이터",
    practiceForm: {
      title: "루나&스타즈 2nd EP 앨범 기획서",
      subtitle: "내부 결재용 · A&R팀 작성 · 20슬라이드 분량 요약본",
      fields: [
        { id: "al_artist", label: "아티스트명", type: "text", placeholder: "예: 루나&스타즈", required: true },
        { id: "al_type", label: "앨범 유형", type: "select", options: ["정규", "미니앨범(EP)", "싱글", "OST"], required: true },
        { id: "al_title", label: "앨범 제목 (가제)", type: "text", placeholder: "예: Starlight Vol.2", required: true },
        { id: "al_date", label: "발매 예정일", type: "date", required: true },
        { id: "al_tracks", label: "수록곡 수", type: "number", placeholder: "예: 6", required: true },
        { id: "al_budget", label: "총 제작 예산 (만원)", type: "number", placeholder: "예: 8000", required: true, hint: "MV 제작비·녹음비·사진촬영비·홍보비 합산 금액입니다." },
        { id: "al_target", label: "타겟 오디언스", type: "text", placeholder: "예: 15~28세 K-POP 팬덤, 국내외", required: true },
        {
          id: "al_concept",
          label: "콘셉트 방향성 (세계관·장르·분위기)",
          type: "textarea",
          placeholder: "앨범 세계관, 장르(BPM 범위 포함), 비주얼 방향, 핵심 메시지를 구체적으로 기술하세요.\n예) 90년대 R&B × 2020년대 드림팝 크로스오버 / 기억·상실·재회 감성 / 우주와 별을 모티프로 한 비주얼 세계관",
          hint: "단순 '밝은 콘셉트'보다 레퍼런스 아티스트(2~3팀)를 포함하면 프로듀서와 디자인팀이 구체적으로 작업할 수 있습니다.",
          required: true,
          maxLength: 600,
        },
        {
          id: "al_ref",
          label: "레퍼런스 아티스트 및 곡",
          type: "textarea",
          placeholder: "콘셉트 방향의 레퍼런스 아티스트 3팀과 참고곡을 기재하세요.\n예) SZA - 'Good Days' (감성), Lorde - 'Liability' (가사 방향), aespa - 'Lucid Dream' (K-POP 구성)",
          required: true,
          maxLength: 400,
        },
        {
          id: "al_kpi",
          label: "성과 목표 (정량 KPI)",
          type: "textarea",
          placeholder: "발매 후 4주 내 목표 수치를 기재하세요.\n예) 멜론 일간차트 TOP 10 진입 / 스포티파이 누적 스트리밍 500만회 / 유튜브 MV 조회수 300만회",
          hint: "KPI가 명확해야 발매 후 성과 분석과 다음 앨범 기획에 데이터로 활용됩니다.",
          required: true,
          maxLength: 400,
        },
        {
          id: "al_risk",
          label: "예상 리스크 및 대응 방안",
          type: "textarea",
          placeholder: "발매 전후 예상 리스크를 기술하세요.\n예) 경쟁 아티스트 동시 발매: 발매일 2주 조정 검토 / 아티스트 컨디션: 녹음 일정 2주 버퍼 확보",
          required: false,
          maxLength: 400,
        },
      ],
    },
  },
  {
    id: "ar2",
    title: "신규 작곡가 데모곡 1차 청취 및 선별",
    detail: "외부 작곡가 제출 데모 14곡 청취. 루나&스타즈 세계관에 부합하는 트랙 3~5곡 추려 프로듀서 리뷰 패스. 장르·BPM·가사 방향성 코멘트 포함 리포트 작성.",
    dueDate: "2026.04.03",
    dDay: 7,
    status: "todo",
    assignee: "이민준 A&R (나)",
    deliverable: "데모 선별 리포트 (Excel)",
    steps: [
      {
        num: 1,
        title: "블라인드 1차 청취 (점수표 작성)",
        detail: "작곡가 이름과 가격 정보를 가린 채로 순수 음악성만 평가합니다. 멜로디 후킹력(30점), 프로덕션 완성도(30점), 아티스트 보이스와의 어울림(40점) 3항목으로 점수화하면 주관이 줄어요.",
        tip: "첫 청취에서 10초 안에 끌리지 않으면 타이틀 후보에서 제외하는 게 좋습니다. 대중은 그 이상 기다려주지 않아요.",
      },
      {
        num: 2,
        title: "앨범 트랙 포지션 검토",
        detail: "선별 후보를 타이틀곡·리드싱글·수록곡으로 분류하고, BPM과 키를 기록합니다. 앨범 전체 흐름상 비슷한 BPM 트랙이 연속되지 않도록 배열을 고려해야 해요.",
      },
      {
        num: 3,
        title: "작곡가에게 2차 수정 가능 여부 확인",
        detail: "선별된 트랙 작곡가에게 가이드 멜로디 교체, 키 조정, 가사 방향 변경 가능 여부를 확인하세요. 원본 유지 고집하는 작곡가는 협업이 어려울 수 있어요.",
        tip: "피드백은 반드시 공식 이메일로만 소통하세요. 카카오톡 메시지는 저작권 분쟁 시 증거로 불리하게 작용할 수 있습니다.",
      },
    ],
    caution: "인기 작곡가 작품이라도 아티스트 이미지와 맞지 않으면 과감히 탈락시키세요. 비용 절감 목적으로 미완성 데모를 타협해서 선택하지 마세요. 나중에 제작비가 훨씬 더 들 수 있습니다.",
    reference: "작곡가 포트폴리오 링크 / 이전 발매작 스트리밍 성과 데이터",
  },
  {
    id: "ar3",
    title: "DarkNova 타이틀곡 피처링 아티스트 섭외",
    detail: "피처링 후보 아티스트 5인 리스트업 및 소속사 컨택. 일정·출연료 협의 후 최우선 후보 1인 확정. 계약 조건 초안 법무팀에 전달.",
    dueDate: "2026.04.10",
    dDay: 14,
    status: "todo",
    assignee: "이민준 A&R (나)",
    deliverable: "피처링 후보 협의 결과 보고",
    steps: [
      {
        num: 1,
        title: "후보 선정 기준 수립 (팬덤 시너지 중심)",
        detail: "팔로워 수보다 '팬덤 결합 시너지'가 핵심입니다. DarkNova 팬층과 겹치지 않는 다른 장르 아티스트를 선택하면 신규 팬 유입 효과가 큽니다. 후보별 팬덤 겹침 비율을 소셜 리슨 툴로 분석하세요.",
        tip: "피처링 상대방의 최근 6개월 음원 성과도 체크하세요. 하락세인 아티스트와의 협업은 기대 이하 효과를 낼 수 있어요.",
      },
      {
        num: 2,
        title: "소속사 공식 컨택",
        detail: "소속사 공식 이메일·팩스로만 협업 제안서를 보냅니다. 개인 SNS DM 먼저 연락하는 것은 업계 비매너입니다. 제안서에는 곡 미리듣기(워터마크 삽입 버전), 예상 일정, 제안 출연료를 포함하세요.",
      },
      {
        num: 3,
        title: "조건 협의 및 계약서 초안 요청",
        detail: "피처링 크레딧 표기 방식(feat. vs with), 성과 기반 추가 보상 조건, 프로모션 의무(인스타 포스팅 횟수 등)를 합의한 뒤 법무팀에 계약서 초안 요청합니다. 구두 합의만으로 작업 시작하지 마세요.",
        tip: "상대 측이 빠른 답변을 원하더라도 법무팀 검토 전 최종 확정 금지. '내부 검토 후 X일 내 회신'으로 시간을 확보하세요.",
      },
    ],
    caution: "피처링 아티스트의 과거 구설수(음주운전, 표절 시비, 논란 발언) 이력을 반드시 확인하세요. 계약 체결 후 이슈 발생 시 음원 취소·재제작 비용이 발생할 수 있습니다.",
    reference: "소속사 공식 홈페이지 연락처 / 이전 피처링 곡 스트리밍 성과 자료",
  },
  {
    id: "ar4",
    title: "외부 작곡가 협업 계약서 체결",
    detail: "선별된 작곡가 2인과 수익 배분(회사 45% / 작곡가 55%) 조건으로 계약 체결. 법무팀 검토 완료 후 서명 진행.",
    dueDate: "2026.04.14",
    dDay: 18,
    status: "todo",
    assignee: "이민준 A&R (나)",
    deliverable: "협업 계약서 서명본",
    approvalTemplateId: "tmpl1",
    steps: [
      {
        num: 1,
        title: "계약 핵심 조건 사전 합의",
        detail: "수익 배분율(업계 평균: 회사 40~50% / 작곡가 50~60%), 저작권 귀속 방식(공동저작 vs 위탁제작), 2차 사용 허락 범위(OST·광고·리믹스), 미사용 시 트랙 반환 여부를 먼저 구두 합의합니다.",
        tip: "수익 배분은 '음원 수익'과 '공연 수익'을 분리해서 명시해야 합니다. 통합으로 쓰면 나중에 분쟁 원인이 돼요.",
      },
      {
        num: 2,
        title: "법무팀 검토 요청",
        detail: "계약서 초안을 법무팀에 전달하고 불공정 조항·독소조항 여부를 검토 받습니다. '영구적 권리', '광범위한 사용 허락' 같은 표현이 있으면 수정 요청하세요.",
      },
      {
        num: 3,
        title: "서명 및 계약금 지급 기안",
        detail: "양측 서명 완료 후 스캔본은 법무팀이, 원본은 사무실 금고에 보관합니다. 계약금 지급은 별도 예산 기안서로 결재를 받아야 하며, 세금계산서 발행 여부도 동시에 확인하세요.",
      },
    ],
    caution: "구두 합의만 있는 상태에서 작업을 시작하는 것은 절대 금지입니다. 반드시 서면 계약 먼저, 작업 시작은 그 이후입니다.",
  },
  {
    id: "ar5",
    title: "무드보드 최종 콘셉트 확정 및 비주얼팀 전달",
    detail: "레퍼런스 이미지 20장 선별, 키워드 5개 도출. 디자인팀·의상팀에 콘셉트 북 전달. 포토카드·커버아트 방향성 합의.",
    dueDate: "2026.04.18",
    dDay: 22,
    status: "todo",
    assignee: "이민준 A&R (나)",
    deliverable: "콘셉트 북 (PDF)",
    steps: [
      {
        num: 1,
        title: "레퍼런스 이미지 수집 (20~30장)",
        detail: "Pinterest, Behance, 인스타그램에서 배경색·조명 톤·패션 스타일·소품 기준으로 카테고리를 나눠 수집합니다. '이 이미지의 어떤 점을 쓰고 싶은지'를 각 이미지에 메모해 두세요.",
        tip: "경쟁 그룹의 최근 앨범 비주얼과 너무 유사한 레퍼런스는 제외하세요. 표절 시비가 없어도 '비슷하다'는 인식이 생기면 독창성을 잃게 됩니다.",
      },
      {
        num: 2,
        title: "팀 투표로 키워드 5개 확정",
        detail: "수집된 이미지에서 공통 무드 키워드를 추출합니다 (예: '청량', '몽환', '도시적', '레트로'). 팀원 과반 동의를 받은 5개로 확정해야 이후 파트별 해석이 일관성을 유지할 수 있어요.",
      },
      {
        num: 3,
        title: "파트별 브리핑 및 방향 합의",
        detail: "디자인팀(커버아트), 의상팀(스타일링), 포토그래퍼(촬영 톤앤매너)에게 동일한 무드보드를 공유하되, 각 파트가 어떻게 해석할지 사전 합의를 진행합니다. 해석 차이로 인한 재작업은 시간과 예산을 크게 소모합니다.",
      },
    ],
  },
];

// ── 공연기획 허브 ─────────────────────────────────────────
export const concertTasks: HubTask[] = [
  {
    id: "ct1",
    title: "DarkNova 출연 계약서 최종 협상 마무리",
    detail: "출연료 5,500만원 / 공연 날짜 6월 14일 / 무대 세팅 요구사항(라이더 리스트 포함) 최종 합의. 법무팀 검토 후 서명 진행.",
    dueDate: "2026.04.01",
    dDay: 5,
    status: "inprogress",
    assignee: "김도현 기획 (나)",
    deliverable: "출연 계약서 서명본",
    approvalTemplateId: "tmpl1",
    steps: [
      {
        num: 1,
        title: "라이더 리스트 항목별 수용 여부 확인",
        detail: "아티스트 측이 요구한 무대 기술 사양(음향·조명·모니터 스피커 수), 케이터링 메뉴, 이동 수단(전용 밴 여부), 분장실 규격을 항목별로 수용 가능 여부를 확인합니다. 불가능한 항목은 대안을 미리 준비하세요.",
        tip: "라이더는 원칙적으로 '요구사항'이지만 협상 대상입니다. 현실적으로 수용 불가한 항목은 계약 전에 명확히 거절하거나 대안을 제시해야 공연 당일 트러블이 없습니다.",
      },
      {
        num: 2,
        title: "협상 포지션 내부 확정",
        detail: "협상 테이블에 앉기 전, 내부에서 최대 양보 한계선을 확정합니다. 출연료 상한(5,800만원), 리허설 시간(2시간 보장), 무대 점유 구간(90분). 협상 중 즉시 결정 권한 없는 항목은 '내부 검토 후 24시간 내 회신' 원칙을 유지하세요.",
      },
      {
        num: 3,
        title: "합의 내용 서면 확인 → 법무팀 계약서 요청",
        detail: "협의된 조건 전체를 이메일로 상대방에게 보내 서면 확인을 받습니다. 그 다음 법무팀에 계약서 작성을 요청하세요. 서명 전 반드시 법무 검토가 완료되어야 합니다.",
      },
    ],
    caution: "출연료 지급 일정을 계약서에 명확히 명시해야 합니다 (예: 계약 시 50%, 공연 3일 전 50%). '공연 후 지급' 조건은 아티스트 측이 거절하는 경우가 많고, 공연 취소 시 반환 조건도 함께 작성하세요.",
    reference: "공연기획협회 출연 계약 표준 양식 / 공연법 관련 규정",
  },
  {
    id: "ct2",
    title: "공연 운영 계획서 제출 (내부 결재)",
    detail: "총 공연 예산 71,000만원 집행 계획, 라인업 확정 현황, 스테이지 구성도, 비상 대응 계획 포함. 대표이사 결재 후 공연장 계약 진행.",
    dueDate: "2026.04.07",
    dDay: 11,
    status: "todo",
    assignee: "김도현 기획 (나)",
    deliverable: "공연 운영 계획서 (30페이지)",
    approvalTemplateId: "tmpl6",
    steps: [
      {
        num: 1,
        title: "각 파트 진행 현황 취합",
        detail: "라인업 확정 현황, 스폰서 계약 진행도, 티켓 판매 현황, 무대 설계 진행 상황을 각 담당자에게 요청해 통합합니다. 현황이 좋지 않은 파트는 리스크로 명시하고 대응 방안을 함께 작성하세요.",
        tip: "담당자들이 바쁘다는 이유로 현황 보고를 늦추는 경우가 많습니다. 수집 마감을 계획서 제출일 3일 전으로 설정하세요.",
      },
      {
        num: 2,
        title: "리스크 매트릭스 작성",
        detail: "예상 리스크(날씨, 아티스트 취소, 장비 고장, 안전사고)를 '발생 가능성 × 영향도' 매트릭스로 정리합니다. 각 리스크마다 대응 플랜을 1개 이상 준비해야 결재 통과가 쉬워집니다.",
      },
      {
        num: 3,
        title: "관할 기관 신고 여부 확인",
        detail: "공연 규모에 따라 지자체 공연 신고, 소방서 안전 점검, 경찰 교통 협조가 필요합니다. 공연 7일 전까지 신고 의무가 있으며, 미신고 시 공연 중단 명령을 받을 수 있습니다.",
      },
    ],
    caution: "관할 지자체 공연 신고는 공연 7일 전까지 완료해야 합니다 (공연법 제6조). 1,000명 이상 공연은 소방청 안전 가이드라인 준수 여부도 별도 확인이 필요합니다.",
    reference: "공연법 제6조 / 소방청 대규모 행사 안전 관리 가이드",
  },
  {
    id: "ct3",
    title: "음향·조명 장비 업체 견적서 수취 (3곳)",
    detail: "삼성사운드, 크리에이티브라이트, 케이스테이지 3개 업체 견적서 접수. 사양 비교표 작성 후 최저가 적정 업체 선정. 계약 조건 협의.",
    dueDate: "2026.04.10",
    dDay: 14,
    status: "todo",
    assignee: "김도현 기획 (나)",
    deliverable: "업체 비교 견적서 (Excel)",
    steps: [
      {
        num: 1,
        title: "RFP(제안요청서) 발송",
        detail: "공연 규모·날짜·장소·필요 장비 사양을 명시한 RFP(제안요청서)를 각 업체에 보냅니다. 견적서 제출 마감일을 필요일로부터 최소 3일 전으로 지정하고, 견적 양식을 동일하게 요구해야 비교가 쉽습니다.",
      },
      {
        num: 2,
        title: "견적서 비교 분석 (단순 가격 비교 금지)",
        detail: "단순 금액 비교가 아닌 장비 사양·기사 수·설치-철수 시간·보험 포함 여부·기존 공연 포트폴리오를 종합 평가합니다. 'A업체 사운드 엔지니어가 얼마나 대형 공연 경험이 있는지'가 실제 퀄리티를 결정합니다.",
        tip: "가능하면 업체의 최근 공연 현장을 직접 참관하거나 레퍼런스 공연 영상을 요청하세요.",
      },
      {
        num: 3,
        title: "최종 업체 선정 및 계약 협의",
        detail: "기술력·가격·납기 균형 기준으로 1순위 업체를 선정합니다. 계약서에 장비 품질 보증 조항(노후 장비 사용 금지, 대체 장비 준비 의무)을 반드시 포함시키세요.",
      },
    ],
    caution: "최저가 업체를 선택할 때는 장비 노후화·현장 경험 미숙 리스크를 반드시 고려하세요. 공연 당일 장비 트러블은 아티스트·관객 모두에게 큰 피해를 줍니다.",
  },
  {
    id: "ct4",
    title: "인스타그램 코리아 스폰서 협상 보고",
    detail: "미디어 파트너 협약 조건 최종 협의. 로고 노출 범위·공식 SNS 공동 포스팅 횟수·독점 콘텐츠 제공 여부 합의. 협상 결과 결재 요청.",
    dueDate: "2026.04.15",
    dDay: 19,
    status: "todo",
    assignee: "김도현 기획 (나)",
    deliverable: "스폰서 협상 결과 보고서",
    approvalTemplateId: "tmpl2",
    steps: [
      {
        num: 1,
        title: "스폰서 패키지 가치 정량화",
        detail: "로고 노출 위치별 예상 노출 수를 계산합니다. 예: 메인 배너(7만 관객 × 2회 = 14만 노출), 공식 SNS 포스팅(팔로워 12만 × 게시물 3개), 현장 MC 멘션(방송 중계 시청 인원 포함). 구체적 숫자로 스폰서의 ROI를 보여주세요.",
        tip: "스폰서사의 마케팅 예산 사이클을 파악하세요. Q2 예산은 보통 1월에 이미 확정됩니다. 타이밍이 늦으면 '내년에 다시 논의하자'는 답이 나올 수 있어요.",
      },
      {
        num: 2,
        title: "협상 미팅 진행",
        detail: "상대방이 가장 원하는 것(독점 콘텐츠? 팬들과의 접점?)을 먼저 파악하고, 우리가 줄 수 있는 것 중 가장 가치 있는 것부터 제안합니다. 가격 협상은 마지막에 하세요.",
      },
      {
        num: 3,
        title: "협상 결과 내부 보고서 작성",
        detail: "합의된 금액, 노출 조건, 독점 콘텐츠 범위, 특약 사항을 정리하고, 미합의 항목이 있다면 재협상 일정과 전략을 포함합니다.",
      },
    ],
    caution: "스폰서 브랜드가 아티스트 이미지와 충돌하는지 사전 검토하세요. 예: 주류 브랜드와 청소년 비율이 높은 K-POP 공연은 이미지 충돌 가능성이 있습니다. 계약 후 이미지 문제 발생 시 해지 조건도 명시해 두세요.",
  },
  {
    id: "ct5",
    title: "티켓 1차 판매 중간 실적 보고",
    detail: "VIP·스탠딩·지정석 판매 현황 집계. 목표 대비 달성률, 환불 건수, 추가 마케팅 필요 여부 분석. 다음 주 이사회 보고 자료 포함.",
    dueDate: "2026.04.20",
    dDay: 24,
    status: "todo",
    assignee: "김도현 기획 (나)",
    deliverable: "티켓 판매 중간 보고서 (PPT)",
    steps: [
      {
        num: 1,
        title: "예매처별 판매 데이터 취합",
        detail: "인터파크·YES24·티켓링크 각 예매처의 관리자 페이지에서 등급별 판매 수·환불 수·잔여석을 가져옵니다. 성별·연령대 구매 분포도 확인하면 마케팅 타겟 재설정에 활용할 수 있어요.",
      },
      {
        num: 2,
        title: "목표 대비 분석 및 추가 조치 검토",
        detail: "전체 점유율이 70% 미달이면 추가 마케팅 예산 편성이 필요합니다. 특정 등급만 미달이라면 가격 조정(얼리버드 연장)이나 패키지 묶음 판매를 검토하세요. 분석 결과에 따른 3가지 조치 옵션을 이사회에 제시해야 합니다.",
        tip: "공연 60일 전 판매율이 50% 미달이면 마케팅 비상 대응 기준으로 봐야 합니다.",
      },
      {
        num: 3,
        title: "이사회 보고 자료 구성",
        detail: "현황 데이터 + 원인 분석 + 추가 조치 옵션 3가지(인플루언서 할인 코드 배포, SNS 광고 증액, 선착순 혜택 추가)를 한 장짜리 요약 슬라이드로 만드세요. 이사회는 긴 자료보다 핵심 판단 정보를 원합니다.",
      },
    ],
  },
];

// ── 재단 허브 ────────────────────────────────────────────
export const foundationTasks: HubTask[] = [
  {
    id: "ft1",
    title: "한국문화예술위원회 보조금 신청서 작성",
    detail: "예술창작지원사업 1억 5천만원 신청. 사업 목표·기대 효과·수혜자 분석·예산 집행 계획표 포함. 문화체육관광부 양식에 맞춰 작성 후 내부 결재.",
    dueDate: "2026.04.02",
    dDay: 6,
    status: "inprogress",
    assignee: "정유나 담당 (나)",
    deliverable: "보조금 신청서 (A4 15장)",
    approvalTemplateId: "tmpl7",
    steps: [
      {
        num: 1,
        title: "공고문 꼼꼼히 분석 (평가 기준 파악)",
        detail: "지원 기관 공고문에서 심사 평가 기준(사업 목적 부합성·예산 적정성·성과 지표 명확성)을 확인합니다. 전년도 선정 사례를 찾아 어떤 사업이 선정됐는지 방향성을 파악하세요.",
        tip: "공고문에 '우선 지원 분야'가 있다면 반드시 그 분야와 연결 짓는 서술을 포함하세요. 심사위원은 공고 목적에 부합하는 사업을 먼저 선정합니다.",
      },
      {
        num: 2,
        title: "사업 목표와 수혜자를 숫자로 구체화",
        detail: "수혜자는 숫자로 명확히 기술합니다: '청년 예술가 50인 지원'. 목표도 측정 가능한 지표로: '전시 개최 5회, 누적 관객 2,000명'. '문화예술 활성화에 기여' 같은 추상적 표현은 감점 요인입니다.",
      },
      {
        num: 3,
        title: "예산 집행 내역 정리",
        detail: "회계 담당자와 협력해 항목별 집행 금액을 확인합니다. 집행률이 40% 미만인 항목은 사유를 명시하세요 (예: '계절성 사업으로 하반기 집행 예정'). 미집행 사유 없이 집행률이 너무 낮으면 감점 요인입니다.",
      },
      {
        num: 4,
        title: "내부 결재 → 온라인 제출",
        detail: "최종본 내부 결재 완료 후 제출합니다. 온라인 신청 시 첨부서류(법인등록증·최근 회계 감사 보고서·사업자등록증)를 미리 PDF로 준비해 두세요. 시스템 오류를 대비해 마감 3일 전에 제출 완료를 목표로 하세요.",
      },
    ],
    caution: "지원금 수령 후 '정산보고서' 제출이 의무입니다. 초기 신청서와 실제 집행이 크게 다르면 반환 요구 또는 향후 신청 제한이 될 수 있어요. 신청서 작성 단계에서 현실적인 계획을 세우는 것이 중요합니다.",
    reference: "한국문화예술위원회 공고문 FAQ / 전년도 선정 사업 성과 보고서 (문화체육관광부 홈페이지 공개)",
    announcement: {
      title: "2026년 예술창작지원사업 공모 공고",
      issuer: "한국문화예술위원회",
      issueDate: "2026.02.14",
      deadline: "2026.04.02 18:00",
      sections: [
        {
          heading: "사업 개요",
          content: "한국문화예술위원회(이하 '위원회')는 「문화예술진흥법」 제7조에 따라 예술 창작 활동의 활성화를 위해 2026년 예술창작지원사업 신청을 공모합니다. 본 사업은 예술단체 및 예술 재단의 창작 역량 강화와 다양한 예술적 시도를 지원하기 위해 마련되었습니다.",
        },
        {
          heading: "지원 대상 및 자격 요건",
          content: "① 비영리 예술단체(사단법인·재단법인 포함)\n② 신청일 기준 법인 설립 2년 이상 경과\n③ 전년도 위원회 지원 사업 정산 완료 기관\n④ 최근 3년 이내 유사 예술사업 실적 보유 단체\n\n※ 동일 사업으로 국가·지방자치단체 등 다른 기관 보조금 중복 신청 불가",
        },
        {
          heading: "지원 내용 및 규모",
          content: "• 총 지원 규모: 30억원 (선정 단체 수: 약 25~30개)\n• 단체당 최대 지원금: 1억 5천만원\n• 자부담 비율: 지원 금액의 20% 이상 (현금·현물 포함)\n• 지원 기간: 2026.06.01 ~ 2026.11.30 (6개월)\n• 지원 분야: 창작지원·향유지원·교육지원 (3개 분야)",
        },
        {
          heading: "심사 기준 (총 100점)",
          content: "① 사업 목적 부합성 (30점): 위원회 사업 취지·문화예술 공공성 기여도, 지역사회 문화 다양성 증진 여부\n② 사업 추진 역량 (25점): 운영 인력, 시설, 최근 3년 사업 성과 및 집행 적정성\n③ 사업 계획의 적절성 (25점): 예산 편성 합리성, 추진 일정 현실성, 정량·정성 성과 목표 명확성\n④ 사회적 가치 창출 (20점): 취약계층 포용성, 지역사회 기여, 청년 예술인 육성, 예술 생태계 기여\n\n※ 총점 70점 미만은 자동 탈락 / 심사위원 3인 이상 합의제 운영",
        },
        {
          heading: "신청 서류 (필수)",
          content: "① 사업계획서 (A4 15매 이내, 별도 양식 없음)\n② 예산명세서 (단가 산출 근거 포함, 엑셀 파일)\n③ 단체 정관 및 법인등록증 사본\n④ 최근 2개년 결산서 또는 감사보고서\n⑤ 전년도 사업 성과 보고서 (해당 단체에 한함)\n⑥ 주요 사업 실적 증빙자료 (최대 5건)\n\n※ 모든 서류는 PDF 변환 후 온라인 시스템에 업로드",
        },
        {
          heading: "신청 방법 및 일정",
          content: "• 접수 기간: 2026.03.03(월) ~ 2026.04.02(수) 18:00까지\n• 접수 방법: 아르코 예술지원시스템 온라인 제출 (www.arko.or.kr)\n• 서류 검토: 2026.04.07 ~ 2026.04.18\n• 현장 실사 (필요 시): 2026.04.21 ~ 2026.04.30\n• 최종 선정 발표: 2026.05.16(금)\n• 협약 체결: 2026.05.26 ~ 2026.05.30\n\n※ 우편·방문 접수 불가 / 마감 시각 이후 접수 불가 (시스템 자동 차단)",
        },
        {
          heading: "유의사항",
          content: "• 허위·과장 기재 발각 시: 지원 즉시 취소 + 5년간 위원회 사업 신청 제한\n• 지원금 수령 후 사업 중도 포기 시: 지원금 전액 반환 + 3년 신청 제한\n• 정산보고서 미제출 시: 차년도 지원 제한 조치\n• 사업 목적 외 집행 발각 시: 지원금 환수 및 법적 조치\n• 성과보고회 참석 의무 (2026.12 예정)\n\n문의: 한국문화예술위원회 창작지원부 (02-760-4524, arts@arko.or.kr)",
        },
      ],
    },
    practiceForm: {
      title: "2026 예술창작지원사업 사업계획서",
      subtitle: "한국문화예술위원회 제출용 · A4 15매 이내 · 별도 양식 없음",
      fields: [
        { id: "p_org", label: "신청 단체명 (법인명)", type: "text", placeholder: "예: 사단법인 K-ArtPEN 문화재단", required: true },
        { id: "p_proj", label: "사업명", type: "text", placeholder: "예: 2026 신진 예술가 창작 레지던시 지원 프로그램", required: true },
        { id: "p_field", label: "신청 분야", type: "select", options: ["창작지원", "향유지원", "교육지원"], required: true },
        { id: "p_amount", label: "신청 금액 (원)", type: "number", placeholder: "예: 150000000", required: true, hint: "자부담 20% 이상 별도 마련 계획을 예산명세서에 포함하세요." },
        { id: "p_period", label: "사업 기간", type: "text", placeholder: "2026.06.01 ~ 2026.11.30", required: true },
        {
          id: "p_purpose",
          label: "1. 사업 목적 및 필요성",
          type: "textarea",
          placeholder: "현재 어떤 문제가 있으며, 이 사업이 왜 필요한지 구체적으로 기술하세요.\n예) 청년 예술인의 창작 공간 부족 실태 + 본 사업의 해결 방안 + 문화예술위원회 취지와의 연결고리",
          hint: "심사 배점 30점 항목입니다. '우리 단체를 위해서'가 아닌 '사회와 예술 생태계에 어떤 공공 가치를 주는가'로 서술해야 고득점을 받습니다.",
          required: true,
          maxLength: 800,
        },
        {
          id: "p_plan",
          label: "2. 사업 내용 및 월별 추진 계획",
          type: "textarea",
          placeholder: "무엇을 어떻게 할 것인지 구체적으로 기술하세요.\n예) 6월: OT 및 입주 작가 선발(10명), 7~9월: 창작 활동 지원(월 3회 멘토링), 10월: 성과 전시회 개최, 11월: 정산 및 보고서 작성",
          hint: "심사위원은 '실제로 실행 가능한가'를 봅니다. 뜬구름 잡는 계획보다 구체적인 날짜·담당자·수량이 있는 계획이 높은 점수를 받습니다.",
          required: true,
          maxLength: 1000,
        },
        {
          id: "p_target",
          label: "3. 수혜 대상 및 예상 성과 (정량 지표 포함)",
          type: "textarea",
          placeholder: "수혜자 수를 숫자로 명시하고 측정 가능한 성과 목표를 기술하세요.\n예) 청년 예술가 50인 지원 / 창작 작품 발표회 5회 개최 / 누적 관람객 2,000명 / 수혜자 만족도 80점 이상",
          hint: "'문화예술 활성화에 기여' 같은 추상적 표현은 감점 요인입니다. 반드시 숫자로 목표를 명시하세요.",
          required: true,
          maxLength: 600,
        },
        {
          id: "p_capacity",
          label: "4. 단체 추진 역량 및 최근 3년 실적",
          type: "textarea",
          placeholder: "유사 사업 운영 경험, 전문 인력 현황(성명·직책·담당 역할), 시설 보유 현황을 기술하세요.\n예) 2023년 청년작가 지원사업 운영(참여 42명, 만족도 87점), 전임 큐레이터 2인 상근, 1,200㎡ 전시 공간 보유",
          hint: "전년도 성과 데이터(참여자 수·만족도 점수)를 수치로 제시하면 설득력이 높아집니다.",
          required: true,
          maxLength: 600,
        },
        {
          id: "p_social",
          label: "5. 사회적 가치 창출 계획",
          type: "textarea",
          placeholder: "취약계층 포용, 지역사회 기여, 청년 예술인 육성, 예술 생태계 기여 측면에서 구체적으로 기술하세요.",
          hint: "심사 배점 20점 항목으로 단순 복지 지원이 아닌 '예술 생태계에 구조적으로 기여'하는 방향을 강조하면 효과적입니다.",
          required: true,
          maxLength: 500,
        },
        {
          id: "p_selfpay",
          label: "6. 자부담 조달 계획 (20% 이상 필수)",
          type: "textarea",
          placeholder: "자부담 금액과 조달 방법을 기술하세요.\n예) 후원금 1,500만원 (삼성문화재단 MOU 체결), 자체 수익 500만원 (입주비 일부 부담), 현물 1,000만원 (시설 임대료 상당액)",
          required: true,
          maxLength: 400,
        },
      ],
    },
  },
  {
    id: "ft2",
    title: "청년 예술가 창작 지원사업 중간 보고서",
    detail: "42명 수혜자 현황, 지원금 집행 내역 8,400만원, 창작 성과물(전시·공연·출판) 현황 포함. 문화체육관광부 제출용 서식 작성.",
    dueDate: "2026.04.05",
    dDay: 9,
    status: "todo",
    assignee: "정유나 담당 (나)",
    deliverable: "중간보고서 (행정 양식 PDF)",
    approvalTemplateId: "tmpl7",
    steps: [
      {
        num: 1,
        title: "수혜자 성과 데이터 수집",
        detail: "각 사업 담당자에게 수혜자 명단·지원 내역·창작 결과물(공연·전시·출판·수상 경력) 현황을 요청합니다. 별도 만족도 설문(5점 척도, 5문항)을 통해 수치화된 만족도 데이터를 수집하면 보고서 설득력이 높아집니다.",
        tip: "수혜자가 성과물 제출에 소극적이면 '향후 추가 지원 시 우선 고려' 인센티브를 안내하세요.",
      },
      {
        num: 2,
        title: "예산 집행 내역 정리",
        detail: "회계 담당자와 협력해 항목별 집행 금액을 확인합니다. 집행률이 40% 미만인 항목은 사유를 명시하세요 (예: '계절성 사업으로 하반기 집행 예정'). 미집행 사유 없이 집행률이 너무 낮으면 감점 요인입니다.",
      },
      {
        num: 3,
        title: "기관 제출 양식에 정확히 기입",
        detail: "문체부 제공 양식의 셀 하나하나에 정확히 기입합니다. 정성적 성과(수혜자 인터뷰 한 줄 인용, 사진 1장)를 추가하면 평가 점수가 올라갑니다. 제출 전 담당자 확인 서명이 반드시 필요합니다.",
      },
    ],
    caution: "수혜자 개인정보(이름·연락처·주소)를 보고서에 무단으로 포함하지 마세요. 개인정보 보호법에 따라 익명 처리하거나 별도 동의서를 받아야 합니다.",
  },
  {
    id: "ft3",
    title: "삼성문화재단 정기 후원 감사 서한 발송",
    detail: "Q1 후원금 1억원 집행 현황 및 수혜 성과 요약. 대표이사 서명 감사 서한 + 사업 성과 브로슈어 동봉. 우편·이메일 동시 발송.",
    dueDate: "2026.04.08",
    dDay: 12,
    status: "todo",
    assignee: "정유나 담당 (나)",
    deliverable: "감사 서한 초안 + 브로슈어",
    steps: [
      {
        num: 1,
        title: "후원 성과를 구체적 숫자로 표현",
        detail: "후원금이 어떻게 쓰였는지 구체적 수치로 서술합니다: '귀 재단의 1억원 후원으로 청년 작가 12명이 첫 개인전을 열었고, 총 3,200명의 시민이 관람했습니다'. 막연한 감사 표현만 있는 서한은 임팩트가 없습니다.",
        tip: "후원자가 가장 뿌듯함을 느끼는 순간은 '내 돈이 실제로 어디에 쓰였는지' 볼 때입니다. 수혜자 사진과 한 줄 후기를 포함하면 효과가 큽니다.",
      },
      {
        num: 2,
        title: "하반기 사업 간략 소개 (추가 후원 요청 금지)",
        detail: "하반기 신규 사업 1~2개를 간략히 소개해 후원자가 지속적인 관심을 갖도록 합니다. 단, 이 서한에서 직접적인 추가 후원 요청은 금물입니다. 후원 요청은 별도 공식 경로를 통해 하세요.",
      },
      {
        num: 3,
        title: "대표이사 서명 수령 및 발송",
        detail: "대표이사 직접 서명을 받습니다 (스탬프·도장은 지양 — 진정성이 낮아 보입니다). 우편은 A4 봉투에 브로슈어를 동봉해 발송하고, 이메일은 PDF 첨부로 동시에 보냅니다. 발송 후 수신 확인 연락 여부도 체크하세요.",
      },
    ],
  },
  {
    id: "ft4",
    title: "장애인 예술인 전시 지원 기획서 내부 결재",
    detail: "전시 일정(9월·10월), 참여 작가 15인 프로필, 전시 공간 섭외 현황, 예산 5,000만원 집행 계획. 이사회 심의 전 내부 결재 필수.",
    dueDate: "2026.04.20",
    dDay: 24,
    status: "todo",
    assignee: "정유나 담당 (나)",
    deliverable: "전시 기획서 (PPT 25슬라이드)",
    approvalTemplateId: "tmpl2",
    steps: [
      {
        num: 1,
        title: "참여 작가 섭외 현황 및 접근성 파악",
        detail: "15인 작가 목록과 장애 유형, 작품 형태, 필요 공간 크기를 정리합니다. 특히 이동 지원이 필요한 작가 수, 작품 수송 방법, 전시장 휠체어 접근성을 사전 파악하는 것이 필수입니다.",
        tip: "장애인 예술인 사업의 핵심은 '작품의 질'이지 '장애'가 아닙니다. 기획서 서술도 작품의 예술적 가치를 먼저 소개하세요.",
      },
      {
        num: 2,
        title: "공간 섭외 및 예산 항목화",
        detail: "전시 공간 3~4곳 후보의 견적을 비교합니다. 작품 수송비, 설치·철수 인건비, 도슨트 인건비, 인쇄물 제작비(브로슈어·안내판)를 항목화하세요. 공간 접근성(지하철역 근거리·주차) 점수도 평가 항목에 포함하세요.",
      },
      {
        num: 3,
        title: "이사회 심의 자료 구성",
        detail: "이사회는 단순히 사업이 좋다는 설명보다 '왜 지금인지, 어떤 리스크가 있고 어떻게 대응할지'를 원합니다. 긍정적 기대 효과와 함께 리스크(작가 중도 포기 대응 방안, 예산 초과 시나리오)를 포함하세요.",
      },
    ],
    caution: "장애인 예술인 관련 사업은 「장애인 예술인 문화예술 활동 지원에 관한 법률」 요건을 사전 확인하세요. 사업 방식에 따라 추가 신고나 요건 충족이 필요할 수 있습니다.",
    reference: "장애인 예술인 문화예술 활동 지원에 관한 법률 / 한국장애인예술인협회 가이드",
  },
  {
    id: "ft5",
    title: "2026 상반기 사업 성과 보고서 초안 작성",
    detail: "총 수혜자 557명, 집행 예산 현황, 각 사업별 성과 지표 달성률, 언론 보도 현황 포함. 하반기 사업 계획 제안 포함.",
    dueDate: "2026.04.28",
    dDay: 32,
    status: "todo",
    assignee: "정유나 담당 (나)",
    deliverable: "성과 보고서 초안 (Excel + Word)",
    steps: [
      {
        num: 1,
        title: "KPI 달성률 집계 (미달 항목도 투명하게)",
        detail: "각 사업별 목표 지표 vs 실적을 대조합니다. 달성률 100% 이상 항목과 미달 항목을 모두 투명하게 기재하세요. 미달 항목에는 반드시 원인 분석을 포함해야 합니다.",
        tip: "성과가 좋은 것만 부각하는 보고서는 신뢰도를 잃습니다. 미달 원인 분석과 개선 방향을 포함해야 전문성 있는 보고서로 인정받습니다.",
      },
      {
        num: 2,
        title: "언론·SNS 노출 현황 정리",
        detail: "보도 기사 목록, SNS 언급량, 유튜브 영상 조회수를 집계합니다. '사회적 가치 환산' 수치(광고 환산 금액, 사회공헌 지수)를 산출하면 후원자 보고 및 보조금 신청에 활용할 수 있어요.",
      },
      {
        num: 3,
        title: "하반기 개선 제안 작성",
        detail: "미달 원인에 대한 구체적인 개선 방향 3가지를 제안합니다. '열심히 하겠습니다' 같은 표현이 아닌 '○○을 ○○으로 변경해 ○○ 효과를 기대한다'는 구체적 실행 계획이어야 합니다.",
      },
    ],
  },
];

// ── IP 스튜디오 허브 ──────────────────────────────────────
export const ipTasks: HubTask[] = [
  {
    id: "ip1",
    title: "어둠 속의 빛 Netflix 글로벌 판권 계약서 검토",
    detail: "8억원 계약 조건 검토. 독점 배급 기간(3년), 2차 저작물 제작권 포함 여부, 해지 조건, 정산 주기 등 조항별 독소조항 체크. 법무팀 검토 필수.",
    dueDate: "2026.03.31",
    dDay: 4,
    status: "inprogress",
    assignee: "최서준 IP담당 (나)",
    deliverable: "계약서 검토 보고서 + 수정 요청 목록",
    approvalTemplateId: "tmpl8",
    steps: [
      {
        num: 1,
        title: "핵심 조항 체크리스트 작성",
        detail: "독점 배급 기간(3년 vs 5년은 수억원의 기회비용 차이), 2차 저작물 제작 동의 범위(드라마→영화 파생 가능 여부), 해지 조건 및 위약금, 정산 주기(월별/분기별)와 정산 기준일을 항목별로 확인합니다.",
        tip: "계약서를 읽을 때 '우리가 포기하는 권리'에 집중하세요. 명시되지 않은 권리를 상대방이 가져가는 경우가 많습니다.",
      },
      {
        num: 2,
        title: "독소조항 발굴",
        detail: "'광범위한 사용 허락' 문구에 주의하세요. 예고 없는 영상 편집·더빙·홍보 광고 삽입을 허용하는 조항이 숨어 있을 수 있습니다. '영구적 권리' 표현은 반드시 삭제를 요구하고, 기간이 명시된 표현으로 교체하세요.",
        tip: "영문 계약서와 국문 번역본 내용이 불일치할 경우 영문본이 우선 적용되는 경우가 많습니다. 번역 오류를 반드시 교차 검토하세요.",
      },
      {
        num: 3,
        title: "수정 요청 목록 우선순위화 및 협상 전략 준비",
        detail: "수정 요청 항목을 우선순위별로 분류하세요: ①반드시 수정(계약 파기 조건), ②가능하면 수정, ③양보 가능. 상대방이 거절할 경우의 대안 조항도 미리 준비해 두면 협상이 빠르게 진행됩니다.",
      },
    ],
    caution: "8억원 계약은 내부 법무팀만으로 부족할 수 있습니다. 외부 엔터테인먼트 전문 변호사 검토도 병행할 것을 강력 권장합니다. 이 비용은 계약 규모 대비 매우 저렴한 보험입니다.",
    reference: "한국저작권위원회 계약서 가이드라인 / OTT 콘텐츠 표준 계약서 샘플 (저작권위원회 제공)",
    practiceForm: {
      title: "OTT 판권 계약서 검토 체크리스트",
      subtitle: "IP팀 검토 보고서 · Netflix 글로벌 판권 계약 (8억원)",
      fields: [
        { id: "ct_work", label: "작품명", type: "text", placeholder: "예: 어둠 속의 빛", required: true },
        { id: "ct_party", label: "협상 상대방", type: "text", placeholder: "예: Netflix Korea (Netflix Inc. 한국 법인)", required: true },
        { id: "ct_amount", label: "계약 금액 (만원)", type: "number", placeholder: "예: 80000", required: true },
        { id: "ct_period", label: "독점 배급 기간", type: "text", placeholder: "예: 계약 체결일로부터 3년", required: true, hint: "3년 vs 5년은 수억원의 기회비용 차이가 납니다. 기간이 길수록 다른 플랫폼 유통이 불가합니다." },
        { id: "ct_region", label: "사용 지역", type: "select", options: ["국내 한정", "아시아", "글로벌(전 세계)", "북미 한정", "아시아-태평양"], required: true },
        {
          id: "ct_rights",
          label: "2차 저작물 제작권 조항 분석",
          type: "textarea",
          placeholder: "계약서 해당 조항 번호와 내용을 인용하고 리스크를 분석하세요.\n예) 제12조: '라이선스사는 본 콘텐츠를 기반으로 파생 콘텐츠를 제작할 권리를 가진다' → 우리 동의 없이 드라마→영화 리메이크 가능 → 삭제 또는 '서면 동의 필수' 조건으로 수정 요청 필요",
          hint: "가장 중요한 항목입니다. 명시되지 않은 권리를 상대방이 가져가는 경우가 많습니다.",
          required: true,
          maxLength: 600,
        },
        {
          id: "ct_toxic",
          label: "독소조항 목록 (발견된 조항 전부 기재)",
          type: "textarea",
          placeholder: "독소조항으로 의심되는 조항 번호와 내용을 기재하세요.\n예) 제8조: '영구적 사용 권리' → 기간 명시 삭제 요청 필요\n제15조: '광범위한 편집·더빙·광고 삽입 허용' → 원작 훼손 가능 → 삭제 또는 사전 서면 동의 조건 추가 필요",
          hint: "'영구적 권리', '광범위한 사용', '일방적 변경 가능' 표현이 보이면 반드시 표시하세요.",
          required: true,
          maxLength: 600,
        },
        {
          id: "ct_settlement",
          label: "정산 조건 검토",
          type: "textarea",
          placeholder: "정산 주기, 기준일, 통화, 원천세 처리 방법을 기재하세요.\n예) 분기별 정산(매 분기 종료 후 45일 이내) / 미국 달러 기준 / 원화 환산 기준일 미명시 → 환율 명시 요청 필요",
          required: true,
          maxLength: 400,
        },
        {
          id: "ct_priority",
          label: "수정 요청 우선순위 (3단계로 분류)",
          type: "textarea",
          placeholder: "① 반드시 수정 (계약 파기 조건): \n② 가능하면 수정: \n③ 양보 가능: ",
          hint: "우선순위가 명확해야 협상 테이블에서 빠르게 진행할 수 있습니다.",
          required: true,
          maxLength: 500,
        },
        {
          id: "ct_opinion",
          label: "IP팀 최종 의견 및 협상 전략 요약",
          type: "textarea",
          placeholder: "계약 체결 권고 여부와 핵심 협상 포인트를 정리하세요.\n예) 수정 조건부 체결 권고 / 핵심 쟁점: 2차 저작물 제작권 삭제 + 정산 기준일 명시",
          required: true,
          maxLength: 400,
        },
      ],
    },
  },
  {
    id: "ip2",
    title: "레전드 오브 미라 캐릭터 상표 출원 보완",
    detail: "특허청 중간 거절이유 통지 대응. '유사 상표 존재' 이유로 식별력 보완 의견서 제출. 변리사 협력하여 지정 상품 범위 조정.",
    dueDate: "2026.04.05",
    dDay: 9,
    status: "todo",
    assignee: "최서준 IP담당 (나)",
    deliverable: "의견서 제출본 (특허청 양식)",
    steps: [
      {
        num: 1,
        title: "거절이유 통지서 분석",
        detail: "특허청 통지서에서 유사 선행상표 목록을 확인하고, 각 선행상표와 우리 상표의 유사점·차별점 분석표를 작성합니다. 완료된 콘셉트·타겟 청취층 데이터를 기반으로 최종 기획서를 작성합니다.",
        tip: "협업은 '우리가 더 받아야 한다'가 아니라 '우리 IP가 당신들에게 이만큼의 가치를 만들어줬다'는 증명입니다.",
      },
      {
        num: 2,
        title: "발매 타임라인 역산 작성",
        detail: "최종 발매일에서 거꾸로 계산합니다. 음원 유통 신청(D-14), 마스터링 완료(D-21), 보컬 레코딩(D-45), 작곡 최종 선정(D-60). 각 단계에 담당자를 명시해야 책임 소재가 분명해져요.",
      },
    ],
    caution: "기존 앨범과 세계관 일관성을 반드시 확인하세요. 팬덤이 쌓아온 세계관을 갑자기 바꾸면 기존 팬층 이탈 원인이 됩니다. 변화가 필요하다면 점진적 전환 서사를 기획서에 포함하세요.",
    reference: "멜론 아티스트 통계 / 한터차트 월간 리포트 / 유사 콘셉트 앨범 가온차트 성과 데이터",
  },
  {
    id: "ip3",
    title: "연재 4개 작품 저작권 침해 모니터링 보고",
    detail: "불법 무료 복제 사이트 11곳 적발, DMCA 테이크다운 요청 발송 현황 정리. 월간 모니터링 리포트 작성 후 법무팀에 공유.",
    dueDate: "2026.04.10",
    dDay: 14,
    status: "todo",
    assignee: "최서준 IP담당 (나)",
    deliverable: "저작권 모니터링 리포트 (월간)",
    steps: [
      {
        num: 1,
        title: "불법 복제 사이트 식별",
        detail: "구글 검색(\"작품명 + 무료\", \"작품명 + raw\")과 전용 모니터링 툴(DMCA Shield, Pixsy)을 활용합니다. 사이트별 복제 회차 범위, 페이지뷰, 운영 국가(한국/해외 여부)를 기록하세요.",
        tip: "검색 시 시크릿 모드를 사용하세요. 개인화된 검색 결과는 불법 사이트를 걸러낼 수 있습니다.",
      },
      {
        num: 2,
        title: "DMCA 테이크다운 발송",
        detail: "침해 URL을 수집해 구글 Search Console에서 DMCA 테이크다운 요청을 제출합니다. 사이트 직접 운영자에게도 침해 중단 요구 이메일을 발송하세요. 국내 불법 사이트는 방송통신심의위원회(kocsc.or.kr) 신고로 빠른 처리가 가능합니다.",
      },
      {
        num: 3,
        title: "리포트 작성 및 법무팀 공유",
        detail: "침해 사이트 목록, DMCA 발송 현황, 처리 결과(삭제 완료/대기 중/거부)를 정리한 월간 리포트를 작성합니다. 침해 금액 추정치도 산출하면 향후 손해배상 청구 시 근거 자료가 됩니다.",
      },
    ],
    caution: "모니터링 결과물(캡처본·URL·날짜 포함 원본 파일)은 최소 2년간 보관하세요. 저작권 소송 시 핵심 증거 자료입니다. 캡처 시 URL 주소창과 날짜가 함께 보이도록 하세요.",
    reference: "국세청 외국납부세액공제 안내 / 각 계약서의 정산 조항",
  },
  {
    id: "ip4",
    title: "수상한 가족 카카오프렌즈 굿즈 계약 갱신",
    detail: "현 계약 만료(5/31) 전 갱신 협의. 로열티율 12% → 15% 상향 제안, 신규 굿즈 카테고리(문구·홈데코) 추가 조건 포함.",
    dueDate: "2026.04.17",
    dDay: 21,
    status: "todo",
    assignee: "최서준 IP담당 (나)",
    deliverable: "계약 갱신 기안서",
    approvalTemplateId: "tmpl8",
    steps: [
      {
        num: 1,
        title: "현 계약 성과 정량화",
        detail: "기존 계약 기간 동안 판매 수량과 로열티 수령액을 집계합니다. IP 인지도 상승 지표(SNS 언급량 증가율, 관련 검색량 성장)도 수치화해 로열티율 인상의 근거로 활용하세요.",
        tip: "협상은 '우리가 더 받아야 한다'가 아니라 '우리 IP가 당신들에게 이만큼의 가치를 만들어줬다'는 증명입니다.",
      },
      {
        num: 2,
        title: "갱신 조건 협상 전략 수립",
        detail: "로열티율 15% 제안 근거: 팬덤 규모 성장, 완결작 IP 안정성, 신규 카테고리(홈데코) 추가로 인한 시장 확대. 상대방이 12% 유지를 원할 경우, 최소 수량 보장 조건 추가를 대안으로 제시하세요.",
      },
      {
        num: 3,
        title: "개정 계약서 작성 및 법무 검토",
        detail: "변경된 조건(로열티율·신규 카테고리·최소 수량 보장)만 개정 계약서로 작성합니다. 로열티 정산 기준이 '판매가 기준인지, 출하가 기준인지' 반드시 명시하세요. 두 기준의 차이가 수령액에 큰 영향을 줍니다.",
      },
    ],
    caution: "현 계약 만료일(5/31)까지 갱신이 안 되면 계약 공백 기간이 발생합니다. 갱신 협상은 만료 2개월 전에 시작하는 것이 원칙입니다.",
  },
  {
    id: "ip5",
    title: "로맨스 클래스 일본 번역 출판 정산 확인",
    detail: "秋田書店 2025년 하반기 정산서 수취 및 검토. 판매 부수·로열티 계산 근거 확인. 원화 환산 금액 장부 기록 후 재무팀에 통보.",
    dueDate: "2026.04.25",
    dDay: 29,
    status: "todo",
    assignee: "최서준 IP담당 (나)",
    deliverable: "정산 확인서 + 환산 명세서",
    steps: [
      {
        num: 1,
        title: "정산서 수취 및 내용 검토",
        detail: "일본어 정산서의 판매 수량, 정산 기준 판매가, 로열티율(계약서와 동일한지), 원천징수 세금(일본 10.21%) 공제 여부를 확인합니다. 이해하기 어려운 항목은 일본 번역 전문가 또는 현지 에이전트에게 도움을 요청하세요.",
        tip: "스폰서사의 마케팅 예산 사이클을 파악하세요. Q2 예산은 보통 1월에 이미 확정됩니다. 타이밍이 늦으면 '내년에 다시 논의하자'는 답이 나올 수 있어요.",
      },
      {
        num: 2,
        title: "협상 미팅 진행",
        detail: "상대방이 가장 원하는 것(독점 콘텐츠? 팬들과의 접점?)을 먼저 파악하고, 우리가 줄 수 있는 것 중 가장 가치 있는 것부터 제안합니다. 가격 협상은 마지막에 하세요.",
      },
      {
        num: 3,
        title: "협상 결과 내부 보고서 작성",
        detail: "합의된 금액, 노출 조건, 독점 콘텐츠 범위, 특약 사항을 정리하고, 미합의 항목이 있다면 재협상 일정과 전략을 포함합니다.",
      },
    ],
    caution: "스폰서 브랜드가 아티스트 이미지와 충돌하는지 사전 검토하세요. 예: 주류 브랜드와 청소년 비율이 높은 K-POP 공연은 이미지 충돌 가능성이 있습니다. 계약 후 이미지 문제 발생 시 해지 조건도 명시해 두세요.",
    reference: "앨범 기획서 (ar1 태스크) / 마케팅팀 채널별 성과 데이터",
  },
];

// ── A&R 허브 직무간 협업 태스크 ──────────────────────────
export const arCollabTasks: HubTask[] = [
  {
    id: "ar-collab1",
    title: "[협업] 앨범 발매 마케팅 전략 공동 기획",
    detail: "마케팅팀과 공동으로 루나&스타즈 2nd EP 발매 마케팅 전략을 수립합니다. A&R 측에서 앨범 콘셉트·타겟 청취층 데이터를 제공하고, 마케팅팀과 채널 전략·예산 배분을 합의합니다.",
    dueDate: "2026.04.09",
    dDay: 13,
    status: "todo",
    assignee: "이민준 A&R (나) × 마케팅팀",
    deliverable: "발매 마케팅 전략서 (공동 작성)",
    isCollaboration: true,
    collaborators: [
      { team: "마케팅팀", role: "채널별 홍보 전략 및 예산 배분 수립", contactTip: "받은 편지함에서 '앨범 마케팅 협업 요청' 메일 발송 후 공동 Google Docs 작업" },
    ],
    steps: [
      {
        num: 1,
        title: "A&R 측 앨범 브리핑 자료 준비",
        detail: "마케팅팀에 전달할 앨범 브리핑 자료를 만듭니다: 콘셉트 키워드 5개, 타겟 청취층(연령·성별·지역), 경쟁 유사 앨범 3개 분석, 예상 스트리밍 목표치. 마케팅팀이 전략을 세울 수 있도록 최대한 구체적으로 작성하세요.",
        tip: "마케팅팀 언어로 번역하세요. '몽환적 R&B' 보다 '멜론 기준 25-34세 여성 타겟, 플레이리스트 삽입 목표'처럼 숫자와 채널 중심으로 쓰세요.",
      },
      {
        num: 2,
        title: "마케팅팀 킣오프 미팅 요청",
        detail: "앨범 브리핑 자료를 첨부해 마케팅팀에 공식 협업 요청 메일을 보냅니다. 미팅 아젠다를 미리 공유하고, 발매 D-30일 이전에 전략을 확정해야 한다는 타임라인을 명시하세요.",
      },
      {
        num: 3,
        title: "채널 전략 합의 및 예산 배분 확정",
        detail: "마케팅팀이 제안한 채널 전략(유튜브 쇼츠, 인스타 릴스, 팬카페 사전 예약 이벤트 등)을 A&R 관점에서 콘셉트 일관성을 검토합니다. 채널별 예산 배분에 서로 동의하면 공동 서명 전략서를 작성하세요.",
        tip: "마케팅 채널이 앨범 콘셉트와 어울리지 않는다고 느끼면 반드시 의견을 내세요. '이 채널은 우리 타겟층보다 10대 비율이 높아 콘셉트와 맞지 않아요'처럼 데이터 기반으로 논의하세요.",
      },
    ],
    caution: "마케팅팀과 A&R이 콘셉트 해석을 다르게 하면 캠페인 방향이 뒤틀릴 수 있습니다. 브리핑 단계에서 서로의 이해를 명확히 맞추는 것이 핵심입니다.",
    reference: "앨범 기획서 (ar1 태스크) / 마케팅팀 채널별 성과 데이터",
  },
  {
    id: "ar-collab2",
    title: "[협업] 외부 작곡가 계약서 법무팀 검토 의뢰",
    detail: "A&R이 작성한 외부 작곡가 협업 계약서 초안을 법무팀에 전달하고, 독소조항·불공정 조항 여부를 검토받습니다. 법무팀 의견을 반영해 최종 계약서를 확정합니다.",
    dueDate: "2026.04.12",
    dDay: 16,
    status: "todo",
    assignee: "이민준 A&R (나) × 법무팀",
    deliverable: "법무 검토 완료 계약서",
    isCollaboration: true,
    collaborators: [
      { team: "법무팀", role: "계약서 조항별 법적 리스크 검토 및 수정 의견 제시", contactTip: "계약서 초안과 '이 조항이 우리에게 불리할 수 있다'는 의심 항목 리스트를 함께 전달" },
    ],
    steps: [
      {
        num: 1,
        title: "계약서 초안 및 배경 자료 정리",
        detail: "법무팀에 전달할 자료를 준비합니다: 계약서 초안 PDF, 상대방 정보(작곡가 이름·소속), 수익 배분 조건, 우리 측 요구사항 메모. 법무팀이 맥락을 이해할 수 있도록 한 페이지 요약을 별도로 첨부하세요.",
      },
      {
        num: 2,
        title: "법무팀 검토 의견 적용",
        detail: "법무팀의 수정 의견을 받으면, '협상 가능한 항목'과 '반드시 수정해야 할 항목'으로 나눕니다. 스폀서 측과의 재협상 시 법무팀이 제시한 대안 조항을 활용하세요.",
      },
      {
        num: 3,
        title: "최종 계약서 확정 및 서명",
        detail: "수정이 완료된 계약서를 법무팀의 최종 확인을 받은 후 서명을 진행합니다. 스폀서 측이 수정을 거부한 조항이 있다면 법무팀과 재논의해 수용 여부를 결정하세요.",
      },
    ],
    caution: "법무팀 검토 없이 계약서에 서명하는 것은 절대 금지입니다. A&R이 독자적으로 판단하기 어려운 법적 리스크(저작권 귀속, 2차 사용 범위 등)가 반드시 존재합니다.",
  },
];

// ── 공연기획 허브 직무간 협업 태스크 ─────────────────────
export const concertCollabTasks: HubTask[] = [
  {
    id: "ct-collab1",
    title: "[협업] 공연 예산안 재무팀 공동 검토",
    detail: "공연기획팀이 작성한 71,000만원 규모 예산안을 재무팀과 공동 검토합니다. 항목별 적정성 검토, 예비비 설정, CFO 최종 결재를 위한 예산 승인 프로세스를 함께 진행합니다.",
    dueDate: "2026.04.05",
    dDay: 9,
    status: "todo",
    assignee: "김도현 기획 (나) × 재무팀",
    deliverable: "재무팀 확인 예산안 (공동 서명본)",
    isCollaboration: true,
    collaborators: [
      { team: "재무팀", role: "예산 항목별 적정성 검토 및 회계 처리 방식 확인", contactTip: "예산 시트(Excel)를 공유 드라이브에 업로드 후 재무팀에 검토 요청 메일 발송" },
    ],
    steps: [
      {
        num: 1,
        title: "예산 시트 공유 준비",
        detail: "항목별로 세분화된 예산 시트를 준비합니다. 각 항목에 담당자, 지급 예정일, 세금계산서 수취 여부를 함께 표시하세요. 재무팀이 빠르게 검토할 수 있도록 항목을 명확하게 구분해 두세요.",
        tip: "예산 시트에는 '왜 이 금액인지' 간단한 메모를 달아두세요. 재무팀은 금액의 근거를 반드시 확인합니다.",
      },
      {
        num: 2,
        title: "재무팀과 예산 리뷰 미팅",
        detail: "항목별 금액의 근거를 설명하고, 재무팀의 회계 처리 방식 질문에 답변합니다. 재무팀이 금액이 크다고 판단하면 '항목 분할 지급'이나 '예비비 조정'으로 협의할 수 있어요.",
      },
      {
        num: 3,
        title: "최종 예산안 CFO 결재 요청",
        detail: "재무팀 확인이 완료된 예산안을 CFO 결재를 위해 제출합니다. 공연기획팀과 재무팀 양측 담당자 서명이 있는 예산안이 결재 통과율이 훨씬 높습니다.",
      },
    ],
    caution: "예산안을 재무팀 검토 없이 직접 CFO에게 올리지 마세요. CFO가 재무팀 확인 여부를 반드시 묻습니다.",
  },
  {
    id: "ct-collab2",
    title: "[협업] 메인 스폰서 계약서 법무팀 협업 검토",
    detail: "서울전자 타이틀 스폰서십 계약서를 법무팀과 심층 협업 검토합니다. 로고 사용권, 독점 조항, 계약 해지 조건 등 리스크 조항을 함께 분석하고 수정 요청 목록을 작성합니다.",
    dueDate: "2026.04.08",
    dDay: 12,
    status: "todo",
    assignee: "김도현 기획 (나) × 법무팀",
    deliverable: "스폰서 계약서 법무 검토 보고서",
    isCollaboration: true,
    collaborators: [
      { team: "법무팀", role: "계약 리스크 조항 분석 및 협상 전략 자문", contactTip: "계약서 초안과 '이 조항이 우리에게 불리할 수 있다'는 의심 항목 리스트를 함께 전달" },
    ],
    steps: [
      {
        num: 1,
        title: "의심 조항 사전 정리",
        detail: "계약서를 먼저 읽고, '이게 맞는 건지 모르겠다'는 조항들을 목록으로 만들어 법무팀에 전달합니다. 조항 번호와 해당 텍스트를 인용하면 법무팀이 훨씬 빠르게 검토할 수 있어요.",
      },
      {
        num: 2,
        title: "법무팀 검토 의견 적용",
        detail: "법무팀의 수정 의견을 받으면, '협상 가능한 항목'과 '반드시 수정해야 할 항목'으로 나눕니다. 스폀서 측과의 재협상 시 법무팀이 제시한 대안 조항을 활용하세요.",
      },
      {
        num: 3,
        title: "최종 계약서 확정 및 서명",
        detail: "수정이 완료된 계약서를 법무팀의 최종 확인을 받은 후 서명을 진행합니다. 스폀서 측이 수정을 거부한 조항이 있다면 법무팀과 재논의해 수용 여부를 결정하세요.",
      },
    ],
    caution: "스폰서 계약에는 공연 취소 시 스폀서비 반환 조건이 반드시 포함되어야 합니다. 법무팀이 이 항목을 반드시 확인할 수 있도록 사전에 알려주세요.",
  },
];

// ── 재단·사업관리 허브 직무간 협업 태스크 ─────────────────
export const foundationCollabTasks: HubTask[] = [
  {
    id: "ft-collab1",
    title: "[협업] 보조금 신청서 법무·회계 적정성 협업 검토",
    detail: "한국문화예술위원회 보조금 신청서를 제출 전 법무팀과 재무팀의 공동 검토를 받습니다. 사업 기술의 법적 표현 적정성, 예산 단가의 회계 기준 부합 여부를 확인합니다.",
    dueDate: "2026.04.01",
    dDay: 5,
    status: "inprogress",
    assignee: "정유나 담당 (나) × 법무팀 × 재무팀",
    deliverable: "법무·재무 확인 완료 신청서",
    isCollaboration: true,
    collaborators: [
      { team: "법무팀", role: "사업 목적과 집행 방식의 법적 표현 검토", contactTip: "신청서 초안 + 지원 기관 공고문을 함께 전달해야 법무팀이 맥락을 이해할 수 있어요" },
      { team: "재무팀", role: "예산 항목별 단가 적정성 및 회계 처리 기준 확인", contactTip: "예산 세부 내역(단가 산출 근거 포함)을 별도 엑셀 파일로 제공하세요" },
    ],
    steps: [
      {
        num: 1,
        title: "법무팀에 법적 표현 검토 요청",
        detail: "사업 내용 설명 중 '법인이 직접 수행'인지 '위탁'인지 구분이 명확해야 합니다. 관련 법령 위반 소지가 있는 표현이 없는지 법무팀 검토를 받으세요. 특히 '지원 대상 선정 기준'이 차별 소지가 없는지 확인이 중요합니다.",
        tip: "법무팀에 '이 표현이 문제 없나요?'라고 물어보기보다, '이 표현 외에 더 안전한 표현이 있나요?'로 물어보면 더 실용적인 답을 받을 수 있어요.",
      },
      {
        num: 2,
        title: "재무팀에 예산 단가 검토 요청",
        detail: "예산 단가가 시장 가격과 크게 다르면 심사에서 감점받습니다. 재무팀에 '강사비 1인 10만원, 인쇄비 1부 5천원' 등 항목별 단가 근거를 확인받으세요. 관리비 비율이 규정 상한선을 초과하지 않는지도 확인이 필요합니다.",
      },
      {
        num: 3,
        title: "검토 의견 반영 및 최종본 확정",
        detail: "법무·재무 양팀의 의견을 모두 반영한 최종본을 작성합니다. 수정사항이 많다면 두 팀에 최종본을 한 번 더 확인받는 것이 안전합니다. 최종 제출 전 담당자 서명란에 양팀 확인 서명을 받으면 내부 결재가 빠릅니다.",
      },
    ],
    caution: "보조금 신청서의 허위 기재나 과장 표현은 향후 지원금 반환 및 3~5년 지원 제한 사유가 됩니다. 법무·재무팀 검토는 형식이 아닌 실질적 내용 검토가 되어야 합니다.",
    reference: "한국문화예술위원회 공고문 / 보조금법 관련 규정",
  },
  {
    id: "ft-collab2",
    title: "[협업] 장애 예술인 전시 홍보 마케팅 콘텐츠 기획",
    detail: "9-10월 장애 예술인 전시 홍보를 위해 마케팅팀과 협업합니다. 전시 취지를 훼손하지 않으면서 일반 대중이 쉽게 접할 수 있는 홍보 콘텐츠를 공동으로 기획합니다.",
    dueDate: "2026.04.25",
    dDay: 29,
    status: "todo",
    assignee: "정유나 담당 (나) × 마케팅팀",
    deliverable: "전시 홍보 콘텐츠 기획안",
    isCollaboration: true,
    collaborators: [
      { team: "마케팅팀", role: "SNS 채널별 홍보 콘텐츠 포맷 및 일정 기획", contactTip: "전시 기획서와 작가 프로필을 제공하면 마케팅팀이 콘텐츠 방향을 잡을 수 있어요" },
    ],
    steps: [
      {
        num: 1,
        title: "전시 기획 의도 마케팅팀에 브리핑",
        detail: "단순 '장애인 예술 전시'가 아닌 '예술적 가치 중심의 전시'임을 마케팅팀에 명확히 전달합니다. 작가들의 작품 이미지와 짧은 작가 스토리를 제공하면 콘텐츠 소재가 됩니다.",
        tip: "마케팅팀에게 '자극적인 홍보는 지양해 달라'고 명확히 요청하세요. 장애를 부각하는 마케팅은 작가들과 관객 모두에게 불쾌감을 줄 수 있습니다.",
      },
      {
        num: 2,
        title: "콘텐츠 방향 합의 및 승인",
        detail: "마케팅팀이 제안한 채널 전략을 검토합니다. '예술적 아름다움 중심', '작가의 목소리 직접 인용', '관객 참여형 이벤트' 방향을 우선순위로 합의하세요.",
      },
      {
        num: 3,
        title: "홍보 일정 및 채널별 실행 계획 확정",
        detail: "전시 오픈 D-30, D-14, D-7, D-Day 기준으로 채널별 홍보 일정을 작성합니다. 인스타그램(작가 인터뷰 릴스), 유튜브(전시 티저 영상), 지역 커뮤니티(타겟 홍보)를 각각 어떻게 운영할지 구체화하세요.",
      },
    ],
  },
];

// ── IP 스튜디오 허브 직무간 협업 태스크 ──────────────────
export const ipCollabTasks: HubTask[] = [
  {
    id: "ip-collab1",
    title: "[협업] Netflix 판권 계약서 법무팀 최종 협업 검토",
    detail: "8억원 규모 Netflix 글로벌 판권 계약서를 법무팀과 심층 협업 검토합니다. IP팀은 비즈니스 조건을 분석하고, 법무팀은 법적 리스크를 분석해 통합 검토 보고서를 작성합니다.",
    dueDate: "2026.03.30",
    dDay: 3,
    status: "inprogress",
    assignee: "최서준 IP담당 (나) × 법무팀",
    deliverable: "통합 계약서 검토 보고서 (IP + 법무 공동)",
    approvalTemplateId: "tmpl8",
    isCollaboration: true,
    collaborators: [
      { team: "법무팀", role: "독소조항·법적 리스크 분석 및 대안 조항 제시", contactTip: "계약서 전문과 함께 'OTT 계약 특수 조항 체크 요청'임을 명시해 전달 — 일반 계약 검토와 다른 전문성이 필요함을 알려주세요" },
    ],
    steps: [
      {
        num: 1,
        title: "IP팀 비즈니스 조건 분석 담당",
        detail: "IP팀은 계약의 '비즈니스 타당성'을 검토합니다: 수익 배분율이 업계 기준에 맞는지, 독점 기간 3년이 적절한지, 글로벌 vs 지역 제한 배급이 우리에게 유리한지를 분석해 의견서를 작성하세요.",
        tip: "유사 OTT 판권 계약 사례(공개된 것)를 찾아 벤치마크로 활용하세요. '업계 평균보다 X% 낮다'는 근거가 협상력을 높입니다.",
      },
      {
        num: 2,
        title: "법무팀 법적 리스크 분석 요청 및 협업",
        detail: "법무팀에 IP팀의 비즈니스 분석 메모를 함께 전달합니다. 법무팀은 비즈니스 맥락을 이해한 상태에서 법적 리스크를 분석할 수 있어요. 분석 완료 후 IP팀·법무팀 공동 미팅에서 쟁점을 정리하세요.",
      },
      {
        num: 3,
        title: "통합 검토 보고서 작성 및 협상 전략 수립",
        detail: "IP팀(비즈니스)과 법무팀(법적)의 의견을 통합한 보고서를 작성합니다. 수정 요청 우선순위: ①반드시 수정(계약 파기 조건), ②가능하면 수정, ③양보 가능. Netflix 측 협상 미팅 전에 이 보고서로 사전 리허설을 하면 좋습니다.",
      },
    ],
    caution: "8억원 계약은 내부 법무팀만으로 부족할 수 있습니다. 외부 엔터테인먼트 전문 변호사 검토도 병행할 것을 강력 권장합니다. 이 비용은 계약 규모 대비 매우 저렴한 보험입니다.",
    reference: "한국저작권위원회 OTT 계약 가이드 / 한국콘텐츠진흥원 콘텐츠 해외 유통 계약 가이드",
  },
  {
    id: "ip-collab2",
    title: "[협업] IP 라이선스 수익 정산 재무팀 공유 및 회계 처리",
    detail: "로맨스 클래스 일본 번역 출판, 수상한 가족 넥슨 게임 IP 등 라이선스 수익 정산 내역을 재무팀에 공식 통보하고, 원화 환산·세금 처리·장부 기록을 공동으로 처리합니다.",
    dueDate: "2026.04.27",
    dDay: 31,
    status: "todo",
    assignee: "최서준 IP담당 (나) × 재무팀",
    deliverable: "IP 수익 정산 명세서 (재무팀 확인본)",
    isCollaboration: true,
    collaborators: [
      { team: "재무팀", role: "외화 수익 원화 환산, 원천세 처리, 장부 기록", contactTip: "정산서 원본(일본어 포함)과 계약서의 정산 조항을 함께 전달 — 재무팀이 직접 확인해야 하는 원본 자료입니다" },
    ],
    steps: [
      {
        num: 1,
        title: "정산 데이터 취합 및 IP팀 1차 검토",
        detail: "모든 라이선스 계약의 정산서를 수취하고, IP팀이 1차로 계산 오류 여부를 확인합니다. 계약서상 로열티율 × 판매량 × 단가를 직접 계산해 수령 예정액과 대조하세요. 오차 5% 이상은 재무팀 통보 전 먼저 상대방에게 소명을 요청해야 합니다.",
        tip: "정산서 수령 즉시 날짜와 금액을 기록하는 습관을 들이세요. 이의제기 기한(보통 30일)을 놓치면 정정이 불가합니다.",
      },
      {
        num: 2,
        title: "재무팀에 정산 내역 공식 통보",
        detail: "IP팀 검토가 완료된 정산 명세서를 재무팀에 공식 전달합니다. 완화 수익은 정산 기준일자를 명시해 재무팀이 올바른 환율을 적용할 수 있도록 하세요. 일본 원천세(10.21%) 공제 여부도 함께 알려주세요.",
      },
      {
        num: 3,
        title: "세금 처리 및 장부 반영 확인",
        detail: "재무팀이 장부에 반영한 후, IP팀에서 금액과 분류가 올바른지 확인합니다. 외국납부세액공제(일본 원천세) 신청 서류 준비 여부도 재무팀과 함께 체크하세요.",
      },
    ],
    caution: "IP 수익은 '영업외 수익'으로 분류되는 경우가 많아 일반 매출과 회계 처리가 다릅니다. 재무팀에 이 점을 미리 알려주세요.",
    reference: "국세청 외국납부세액공제 안내 / 각 계약서의 정산 조항",
  },
];
